import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { LeagueProjectionAttributes } from '@library/types/leagues';
import type { PlayerDescriptors } from '@library/types/player';
import type { StringKeyNumberValue } from '@bo-types/common';
import { generateBaseProjection } from '@bo-utils/trading';
import { getCalculatedBoardStartTimes } from '@bo-utils/leagueTimes';
import { isStandard } from '@library/utils';

export const copyProjectionSplit: StringKeyNumberValue = {
  CBB1H: 0.5,
  CBB2H: 0.5,
  CFB1H: 0.5,
  CFB2H: 0.5,
  CFB4Q: 0.25,
  NBA1H: 0.5,
  NBA2H: 0.5,
  NBA1Q: 0.25,
  NBA4Q: 0.25,
  NFL1H: 0.5,
  NFL2H: 0.5,
  NFL4Q: 0.25,
  NHL1P: 0.33,
  NHL2P: 0.33,
  NHL3P: 0.33,
  SOCCER1H: 0.5,
  SOCCER2H: 0.5,
  WNBA1H: 0.5,
  WNBA2H: 0.5,
  WNBA1Q: 0.25,
  WNBA4Q: 0.25
};

export const getFilteredCopiedProjections = ({
  copiedProjections,
  durationId,
  durations,
  fetchedProjections,
  gameStartTime,
  leagueName,
  playerDescriptors,
  statTypes
}: {
  copiedProjections: Array<GamePlayerProjectionTrading>;
  durationId: string;
  durations: Array<LeagueProjectionAttributes>;
  fetchedProjections: Array<GamePlayerProjectionTrading>;
  gameStartTime: string | undefined;
  leagueName: string;
  playerDescriptors: PlayerDescriptors;
  statTypes: Array<LeagueProjectionAttributes>;
}): Array<GamePlayerProjectionTrading> => {
  const { boardTime: calculatedBoardTime, startTime: calculatedStartTime } =
    getCalculatedBoardStartTimes({
      durationId,
      durations,
      gameStartTime,
      leagueName
    });

  const filteredCopiedProjections = copiedProjections.length
    ? copiedProjections
        .filter((copyProjection) => {
          const { isPromo, statTypeId, oddsType } = copyProjection;

          if (isPromo || !isStandard(oddsType)) {
            return false;
          }

          const isStatInFetchedProjections = fetchedProjections?.some(
            (projection) => projection.statTypeId === statTypeId
          );

          if (isStatInFetchedProjections) {
            return false;
          }

          const isStatExistsInLeagueStatTypes = statTypes.some(
            (leagueStatType) =>
              leagueStatType.leagueProjectionFilterId === statTypeId
          );

          if (!isStatExistsInLeagueStatTypes) {
            return false;
          }

          return true;
        })
        .map((projection) => {
          const {
            description,
            foreignId,
            gameId,
            position = '',
            projectionValue,
            statTypeId,
            statType,
            team,
            teamName
          } = projection;

          return generateBaseProjection({
            boardTime: calculatedBoardTime,
            dataSource: 'manual',
            description,
            durationId,
            foreignId,
            gameId,
            startTime: calculatedStartTime,
            displayTime: projection.displayTime,
            isCopied: true,
            playerDescriptors,
            playerId: '',
            position,
            projectionValue: projectionValue
              ? projectionValue * (copyProjectionSplit[leagueName] ?? 1)
              : '',
            statTypeId,
            statType,
            statTypes: [],
            status: 'removed',
            team,
            teamName
          });
        })
    : [];

  return filteredCopiedProjections;
};
